/* =====================================================================
 * DMMentionNotice — 통합에이전트 그룹채팅 @멘션 알림 카드
 *
 * 그룹채팅에서 A가 B를 멘션하면 A↔B 1:1 DM에 mention_event 메타데이터를
 * 가진 메시지가 발송된다. 이 카드는 그 메시지를 "OO님이 회원님을
 * 멘션했어요" 알림 + 바로가기 버튼으로 렌더링하고, 바로가기 클릭 시
 * /agents/{item_id}#m-{message_id} 로 이동해 멘션된 메시지 위치로
 * 스크롤한다 (useScrollToHashMessage).
 * ===================================================================== */

import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import styled, { css } from 'styled-components';
import { ArrowUpRight, AtSign } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

export interface MentionEventMeta {
  item_id: string;
  message_id: string;
  sender_id: string;
  sender_name?: string;
  group_title?: string;
  preview?: string;
}

export function parseMentionEvent(metadata: Record<string, unknown> | undefined): MentionEventMeta | null {
  const raw = metadata?.mention_event;
  if (!raw || typeof raw !== 'object') return null;
  const ev = raw as Record<string, unknown>;
  const itemId = typeof ev.item_id === 'string' ? ev.item_id.trim() : '';
  const messageId = typeof ev.message_id === 'string' ? ev.message_id.trim() : '';
  if (!itemId || !messageId) return null;
  return {
    item_id: itemId,
    message_id: messageId,
    sender_id: typeof ev.sender_id === 'string' ? ev.sender_id : '',
    sender_name: typeof ev.sender_name === 'string' ? ev.sender_name : undefined,
    group_title: typeof ev.group_title === 'string' ? ev.group_title : undefined,
    preview: typeof ev.preview === 'string' ? ev.preview : undefined,
  };
}

interface Props {
  event: MentionEventMeta;
  /** 현재 유저가 멘션한 당사자(발신자)인지 여부 */
  isMine: boolean;
  /** 상대방 표시 이름 (발신자 시점에서 멘션 대상 이름으로 사용) */
  otherName?: string | null;
  createdAt?: number;
}

const DMMentionNotice: React.FC<Props> = ({ event, isMine, otherName, createdAt }) => {
  const { t } = useTranslation('common');
  const nav = useNavigate();

  const noticeText = isMine
    ? t('dm.mentionNoticeSent', { name: otherName || t('dm.teamMember') })
    : t('dm.mentionNotice', { name: event.sender_name || t('dm.teamMember') });

  const timeLabel = useMemo(() => {
    if (!createdAt) return null;
    try {
      return new Date(createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } catch {
      return null;
    }
  }, [createdAt]);

  const handleGoTo = () => {
    nav(`/agents/${event.item_id}#m-${encodeURIComponent(event.message_id)}`);
  };

  return (
    <Row $mine={isMine}>
      <Card $mine={isMine}>
        <Head>
          <IconWrap>
            <AtSign size={13} />
          </IconWrap>
          <NoticeText>{noticeText}</NoticeText>
        </Head>
        {event.group_title && <GroupTitle>{event.group_title}</GroupTitle>}
        {event.preview && <Preview>{event.preview}</Preview>}
        <GoToBtn type="button" onClick={handleGoTo}>
          {t('dm.mentionGoTo')}
          <ArrowUpRight size={13} />
        </GoToBtn>
      </Card>
      {timeLabel && <Time>{timeLabel}</Time>}
    </Row>
  );
};

export default DMMentionNotice;

/* ---- Styled ---- */

const Row = styled.div<{ $mine: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: ${({ $mine }) => ($mine ? 'flex-end' : 'flex-start')};
  gap: 3px;
  padding: 2px 0;
`;

const Card = styled.div<{ $mine: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-width: 320px;
  padding: 12px 14px;
  border-radius: 14px;
  border: 1px solid ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)'};
  background: ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(255,255,255,0.04)' : 'rgba(255,255,255,0.85)'};
  box-shadow: ${({ theme }) =>
    theme.mode === 'dark'
      ? '0 1px 4px rgba(0,0,0,0.25)'
      : '0 1px 4px rgba(0,0,0,0.05)'};

  ${({ $mine }) =>
    $mine
      ? css`border-bottom-right-radius: 4px;`
      : css`border-bottom-left-radius: 4px;`}
`;

const Head = styled.div`
  display: flex;
  align-items: center;
  gap: 7px;
`;

const IconWrap = styled.div`
  flex-shrink: 0;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(99,102,241,0.22)' : 'rgba(99,102,241,0.12)'};
  color: #6366f1;
`;

const NoticeText = styled.span`
  font-size: 0.85rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textPrimary};
  line-height: 1.4;
`;

const GroupTitle = styled.span`
  font-size: 0.72rem;
  color: ${({ theme }) => theme.colors.textMuted};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const Preview = styled.div`
  font-size: 0.78rem;
  color: ${({ theme }) => theme.colors.textSecondary};
  line-height: 1.45;
  padding: 6px 10px;
  border-left: 2px solid ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(255,255,255,0.14)' : 'rgba(0,0,0,0.1)'};
  background: ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.025)'};
  border-radius: 0 8px 8px 0;
  word-break: break-word;
`;

const GoToBtn = styled.button`
  align-self: flex-start;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 5px 12px;
  border: none;
  border-radius: 999px;
  background: ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(99,102,241,0.25)' : 'rgba(99,102,241,0.1)'};
  color: ${({ theme }) => (theme.mode === 'dark' ? '#a5b4fc' : '#4f46e5')};
  font-size: 0.76rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s ease;

  &:hover {
    background: ${({ theme }) =>
      theme.mode === 'dark' ? 'rgba(99,102,241,0.38)' : 'rgba(99,102,241,0.18)'};
  }
`;

const Time = styled.span`
  font-size: 0.66rem;
  color: ${({ theme }) => theme.colors.textMuted};
  padding: 0 4px;
`;
